import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { BasicSelect } from '@tradingblock/components';
import dayjs from 'dayjs';
import { useDispatch } from 'react-redux';
import _ from 'lodash';
import { useBlockData, useBlockId } from '../../components/blocks/BlockState';
import { InfoDataActions } from '../../data/global/actions/InfoActions';
import { useStateSelector } from '../../data/global/dataSelectors';
import { MarketNews } from '@tradingblock/types';
import { useGroupAssetSymbol } from '../useGroupState';
import { useNews } from '../../hooks/useNews';
import { SymbolChannels } from '../../types';
import { useInfoSettings } from './hooks/useInfoSettings';
import { DJISymbols, NASDAQSymbols, SP500Symbols } from './utils/MarketSymbols';

export const MarketNewsTab: React.FunctionComponent<{}> = ({}) => {
  const allBlockData: {} = useBlockData();
  const { subscribeBlockSymbols } = useNews();
  const groupAsset = useGroupAssetSymbol();
  const blockId = useBlockId();
  const dispatch = useDispatch();
  const { symbolRequest, filteredNewsSymbols } = useStateSelector(
    s => s.information.marketNews[blockId] || { data: [] }
  );
  const newsFeedStatus = useStateSelector(s => s.newsFeedProvider.status);
  const cachedNews = useStateSelector(s => s.information.cachedMarketNews);
  const blocksInfo = useStateSelector(s => s.blocks.blocks);
  const block = blocksInfo.find(b => b.blockId === blockId);
  const storedBlockNewsSymbols = block && block.data && block.data.news;
  const storedBlockFilteredNewsSymbols = block && block.data && block.data.filteredNews;
  const storedBlockPageSize = block && block.data && block.data.pageSize;
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(_.isUndefined(storedBlockPageSize) ? 10 : storedBlockPageSize);
  const settings = useInfoSettings();

  const channelMappings = [
    { active: settings.dailyNewsfeed, name: 'DailyNewsfeed' },
    { active: settings.corporateActionsDividends, name: 'CorporateActionsDividends' },
    { active: settings.earningsReports, name: 'EarningsReports' },
    { active: settings.rumorsAndIdeas, name: 'RumorsAndIdeas' },
    { active: settings.companyNews, name: 'CompanyNews' },
    { active: settings.sectorNews, name: 'SectorNews' },
    { active: settings.ipos, name: 'Ipos' },
    { active: settings.economicsAndGovernment, name: 'EconomicsAndGovernment' },
    { active: settings.theMarkets, name: 'TheMarkets' },
    { active: settings.etfCoverage, name: 'EtfCoverage' },
    { active: settings.options, name: 'Options' },
  ];

  const channelArray: string[] = [];
  // Populate channelArray for MarketNews polling request based on useInfoSettings()
  if (_.isEmpty(settings)) {
    channelArray.push('DailyNewsfeed');
  } else {
    channelMappings.forEach(channelObj => {
      if (channelObj.active) channelArray.push(channelObj.name);
    });
  }

  useEffect(() => {
    let blockNews: MarketNews[] = [];
    let symbolChannels: SymbolChannels[] = symbolRequest;

    // update block with new symbols/channels/news
    if (cachedNews.length > 0 && symbolRequest && symbolRequest.length > 0) {
      // cycle through cached news and add to the news block array
      cachedNews.forEach(cn => {
        const match = symbolChannels.find((sym: SymbolChannels) => cn.stocks && cn.stocks.includes(sym.name));
        let newsChannelsLowerCase = cn.newsChannels ? cn.newsChannels.map(n => n.toLowerCase()) : [];
        let added = false;
        // match symbol found in news
        if (match) {
          let inFilter = filteredNewsSymbols.length > 0 ? match && filteredNewsSymbols.includes(match.name) : true;
          if (inFilter)
            for (let ind = 0; ind < match.channels.length; ind++) {
              if (newsChannelsLowerCase.includes(match.channels[ind].toLowerCase())) {
                added = true;
                break;
              }
            }
        }

        if (!added) {
          // match symbol not found in news

          // check against the DJIA index
          const foundBlockDJIA = symbolChannels.find(s => s.name === '$DJIA');
          if (foundBlockDJIA) {
            let inFilter =
              filteredNewsSymbols.length > 0
                ? foundBlockDJIA && filteredNewsSymbols.includes(foundBlockDJIA.name)
                : true;
            if (inFilter)
              DJIAForLoop: for (let i = 0; i < DJISymbols.length; i++) {
                if (cn.stocks && cn.stocks.includes(DJISymbols[i])) {
                  let channelInd = 0;
                  while (!added && channelInd < foundBlockDJIA.channels.length) {
                    if (newsChannelsLowerCase.includes(foundBlockDJIA.channels[channelInd].toLowerCase())) {
                      added = true;
                      break DJIAForLoop;
                    }
                    channelInd++;
                  }
                }
              }
          }

          // check against the DJI index
          const foundBlockDJI = symbolChannels.find(s => s.name === '$DJI');
          if (foundBlockDJI && !added) {
            let inFilter =
              filteredNewsSymbols.length > 0 ? foundBlockDJI && filteredNewsSymbols.includes(foundBlockDJI.name) : true;
            if (inFilter)
              DJIForLoop: for (let i = 0; i < DJISymbols.length; i++) {
                if (cn.stocks && cn.stocks.includes(DJISymbols[i])) {
                  let channelInd = 0;
                  while (!added && channelInd < foundBlockDJI.channels.length) {
                    if (newsChannelsLowerCase.includes(foundBlockDJI.channels[channelInd].toLowerCase())) {
                      added = true;
                      break DJIForLoop;
                    }
                    channelInd++;
                  }
                }
              }
          }

          // check against the SPX index
          const foundBlockSPX = symbolChannels.find(s => s.name === '$SPX');
          if (foundBlockSPX && !added) {
            let inFilter =
              filteredNewsSymbols.length > 0 ? foundBlockSPX && filteredNewsSymbols.includes(foundBlockSPX.name) : true;
            if (inFilter)
              SP500ForLoop: for (let i = 0; i < SP500Symbols.length; i++) {
                if (cn.stocks && cn.stocks.includes(SP500Symbols[i])) {
                  let channelInd = 0;
                  while (!added && channelInd < foundBlockSPX.channels.length) {
                    if (newsChannelsLowerCase.includes(foundBlockSPX.channels[channelInd].toLowerCase())) {
                      added = true;
                      break SP500ForLoop;
                    }
                    channelInd++;
                  }
                }
              }
          }

          // check against the VIX index
          const foundBlockVIX = symbolChannels.find(s => s.name === '$VIX');
          if (foundBlockVIX && !added) {
            let inFilter =
              filteredNewsSymbols.length > 0 ? foundBlockVIX && filteredNewsSymbols.includes(foundBlockVIX.name) : true;
            if (inFilter)
              SP500ForLoop: for (let i = 0; i < SP500Symbols.length; i++) {
                if (cn.stocks && cn.stocks.includes(SP500Symbols[i])) {
                  let channelInd = 0;
                  while (!added && channelInd < foundBlockVIX.channels.length) {
                    if (newsChannelsLowerCase.includes(foundBlockVIX.channels[channelInd].toLowerCase())) {
                      added = true;
                      break SP500ForLoop;
                    }
                    channelInd++;
                  }
                }
              }
          }

          // check against the NDX indexes
          const foundBlockNDX = symbolChannels.find(s => s.name.includes('$NDX'));
          if (foundBlockNDX && !added) {
            let inFilter =
              filteredNewsSymbols.length > 0 ? foundBlockNDX && filteredNewsSymbols.includes(foundBlockNDX.name) : true;
            if (inFilter)
              NASDAQForLoop: for (let i = 0; i < NASDAQSymbols.length; i++) {
                if (cn.stocks && cn.stocks.includes(NASDAQSymbols[i])) {
                  let channelInd = 0;
                  while (!added && channelInd < foundBlockNDX.channels.length) {
                    if (newsChannelsLowerCase.includes(foundBlockNDX.channels[channelInd].toLowerCase())) {
                      added = true;
                      break NASDAQForLoop;
                    }
                    channelInd++;
                  }
                }
              }
          }
        }

        // add to block
        if (added) blockNews.push(cn);
      });
    }

    setAllItems(blockNews);
  }, [cachedNews, symbolRequest, filteredNewsSymbols]);

  useEffect(() => {
    let symbolChannelMatrix: SymbolChannels[] = [];
    let filteredNewsSymbols: string[] = [];
    let symbols = ['META', 'AAPL', 'AMZN', 'NFLX', 'GOOG', 'MSFT'];
    let useDefault = true;

    if (storedBlockNewsSymbols) {
      symbolChannelMatrix = storedBlockNewsSymbols;
      useDefault = false;
    }

    if (storedBlockFilteredNewsSymbols) filteredNewsSymbols = storedBlockFilteredNewsSymbols;

    if (useDefault) {
      symbols.forEach(
        symbol => (symbolChannelMatrix = [...symbolChannelMatrix, { name: symbol, channels: channelArray }])
      );
    }

    dispatch(
      InfoDataActions.addBlockToMarketNews({
        blockId: blockId,
        symbols: symbolChannelMatrix,
        filteredNewsSymbols: filteredNewsSymbols,
      })
    );
  }, []);

  useEffect(() => {
    if (symbolRequest && newsFeedStatus && newsFeedStatus === 'open') subscribeBlockSymbols(blockId, symbolRequest);
  }, [symbolRequest, newsFeedStatus]);

  const [allItems, setAllItems] = useState<MarketNews[]>([]);

  // Refs for restoring article list scroll position and scrolling to top of article element when article is being displayed
  const articleRefs: any = useRef([]);
  const scrollRef: any = useRef(null);

  // executeScroll
  // @DESC    based on index of clicked item, scroll to the ref w/ corresponding index
  // @PARAMS  index - Position of reference in articleRefs array, saved in localStorage on article click and retrieved where executeScroll is called
  const executeScroll = (index: number) => {
    articleRefs.current[index].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  // executeTopScroll
  // @DESC    scroll to the scrollRef div, located at the top of the article element. called when article is set and displayed to bring user to top of text
  // @PARAMS  NONE
  const executeTopScroll = () => {
    scrollRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  const [article, setArticle] = React.useState<undefined | MarketNews>();

  // Get Market News
  useEffect(() => {
    //   // Set page to 1 for initial requests
    setPage(1);
    //   // dispatch(InfoDataActions.stopPollingForMarketNews());

    if (groupAsset) {
      if (article !== undefined) {
        setArticle(undefined);
      }
    } else {
      if (article !== undefined) {
        setArticle(undefined);
      }
    }

    articleRefs.current = articleRefs.current.slice(0, allItems.length);
  }, [groupAsset]);

  // handleListItemClick
  // @DESC    sets localStorage item 'articleElement' to the index of article within allItems array, updates article state with API data, and scrolls to top of article text
  // @PARAMS  article - individual article data from allItems.map, articleEl - index of the article within allItems array, which corresponds to the articleRef index of the article
  const handleListItemClick = (article: MarketNews, articleEl: number) => {
    localStorage.setItem('articleElement', JSON.stringify(articleEl));
    setArticle(article);
    executeTopScroll();
  };

  // handleBackClick
  /* @DESC    updates article state with undefined, rendering the article list, 
    retrieves localStorage item 'articleElement' containing the index of the article that was previously clicked, 
    scrolls the user back to that article within the article list  */
  // @PARAMS  NONE
  function handleBackClick() {
    setArticle(undefined);
    let index: any = localStorage.getItem('articleElement');
    setTimeout(function() {
      executeScroll(index);
    }, 50);
  }

  const paginate = (array: MarketNews[], pageSize: number, pageNumber: number) => {
    return array.slice((pageNumber - 1) * pageSize, pageNumber * pageSize);
  };

  const getPageCount = (array: MarketNews[], pageSize: number) => {
    return Math.ceil(array.length / pageSize);
  };

  const pageSizes = [5, 10, 15, 25];

  const pageCount = getPageCount(allItems, pageSize);

  const pageSizeOptions = useMemo(
    () => _.map(pageSizes, s => ({ value: s, label: s ? `${s} per page` : 'All pages' })),
    []
  );

  const pageSizeLabel = useMemo(() => {
    const label = _.find(pageSizeOptions, o => o.value === pageSize);
    return label ? label.label : '';
  }, [pageSize, pageSizeOptions]);

  const onPageSizeChanged = useCallback(
    (pageSizeOption: { value: number }) => {
      setPageSize(pageSizeOption.value);
      setPage(1);
    },
    [setPage, setPageSize]
  );

  if (allItems.length === 0 || !newsFeedStatus || newsFeedStatus !== 'open')
    return (
      <>
        <p className="mute">
          <i className="fal fa-search" /> No articles found... Please adjust search parameters.
        </p>
      </>
    );
  return (
    <>
      {allItems && (
        <>
          <div className="scroll" ref={scrollRef} />
          {!article && (
            <>
              <ul className="news">
                {paginate(allItems, pageSize, page).map((item, index) => (
                  <li
                    className={`news-item marketnews-${item.id}`}
                    key={`marketnews-${index}`}
                    id={item.id.toString()}
                    ref={el => (articleRefs.current[index] = el)}
                  >
                    <div className="news-title">
                      <div className="news-title-icon">
                        <i className="fal fa-newspaper"></i>
                      </div>
                      <div className="news-title-summary">
                        <p>
                          <strong>
                            <a
                              className="news-title-link"
                              onClick={() => {
                                handleListItemClick(
                                  {
                                    author: item.author,
                                    id: item.id,
                                    title: item.title,
                                    body: item.body,
                                    updated: item.updated,
                                    stocks: item.stocks,
                                    channels: item.channels,
                                    newsChannels: item.newsChannels,
                                  },
                                  index
                                );
                              }}
                              href="#"
                            >
                              {item.title}
                            </a>
                          </strong>
                        </p>
                        <p className="news-title-details">
                          <span className="time-stamp" key={`timestamp-${index}`}>
                            {dayjs(item.updated).format('ddd, DD MMM YYYY hh:mm:ssa ZZ UTC')}
                          </span>
                          {item.stocks && (
                            <>
                              {item.stocks.map((item, index) => (
                                <span className="stocks" key={`stocks-${index}`}>
                                  {item}
                                </span>
                              ))}
                            </>
                          )}
                        </p>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="table-toolbar table-toolbar-bottom">
                <div className="table-toolbar-primary">
                  {pageSize !== 0 && (
                    <span className="paging">
                      <button
                        className="btn btn-blend"
                        onClick={() => {
                          setPage(1);
                          executeTopScroll();
                        }}
                        title="First Page"
                      >
                        <i className="fal fa-angle-double-left" />
                      </button>
                      <button
                        className="btn btn-blend"
                        onClick={() => {
                          if (page - 1 >= 1) {
                            setPage(page - 1);
                            executeTopScroll();
                          }
                        }}
                        title="Previous page"
                      >
                        <i className="far fa-angle-left" />
                      </button>
                      <span className="paging-current">
                        {`${page} of ${pageCount} page`}
                        {pageCount !== 1 ? 's' : ''}
                      </span>
                      <button
                        className="btn btn-blend"
                        onClick={() => {
                          if (page + 1 <= pageCount) {
                            setPage(page + 1);
                            executeTopScroll();
                          }
                        }}
                        title="Next page"
                      >
                        <i className="far fa-angle-right" />
                      </button>
                      <button
                        className="btn btn-blend"
                        onClick={() => {
                          setPage(pageCount);
                          executeTopScroll();
                        }}
                        title="Last page"
                      >
                        <i className="far fa-angle-double-right" />
                      </button>
                    </span>
                  )}
                </div>
                <div className="table-toolbar-secondary">
                  <BasicSelect
                    className="btn btn-dark btn-tall dropdown-toggle text-left"
                    options={pageSizeOptions}
                    placeholder={pageSizeLabel}
                    onChange={onPageSizeChanged}
                    isClearable={false}
                    isSearchable={false}
                  />
                </div>
              </div>
            </>
          )}

          {!!article && (
            <>
              <div className="article_pane">
                <p>
                  <button className="btn btn-outline-light" onClick={handleBackClick}>
                    <i className="fal fa-angle-left"></i> Back to all news
                  </button>
                </p>
                <div className="news-title">
                  <div className="news-title-icon">
                    <i className="fal fa-newspaper"></i>
                  </div>
                  <div className="news-title-summary">
                    <p>
                      <strong>
                        {article.title} -- {article.author}
                      </strong>
                    </p>
                    <p className="news-title-details">
                      <span className="time-stamp">
                        {' '}
                        {dayjs(article.updated).format('ddd, DD MMM YYYY hh:mm:ssa ZZ UTC')}
                      </span>
                      {article.stocks && (
                        <>
                          {article.stocks.map((item, index) => (
                            <span className="stocks" key={`stocks-${index}`}>
                              {item}
                            </span>
                          ))}
                        </>
                      )}
                    </p>
                  </div>
                </div>

                <div
                  className="news-title-body"
                  dangerouslySetInnerHTML={{
                    __html: article.body,
                  }}
                />
              </div>
            </>
          )}
        </>
      )}
    </>
  );
};
